import {
  AddRounded as AddIcon,
  DeleteOutlineRounded as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import { useDispatch, useSelector } from "react-redux";
import * as glClassifications from "store/glClassifications";
import { v4 as uuidv4 } from "uuid";

const newRow = () => ({
  id: uuidv4(),
  classification: "",
  premiumBasis: "s",
  exposureAmount: null,
  advancePremium: null,
});

const fmtRate = (value) => {
  if (value == null || !Number.isFinite(value)) return "";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const GLClassificationsTable = () => {
  const dispatch = useDispatch();
  const rows = useSelector(glClassifications.selectRows);

  const handleAdd = () => dispatch(glClassifications.addRow(newRow()));
  const handleRemove = (id) => dispatch(glClassifications.removeRow(id));
  const handleUpdate = (id, values) =>
    dispatch(glClassifications.updateRow({ id, values }));

  return (
    <Paper variant={"outlined"} sx={{ p: 2, mt: 2 }}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ mb: 1 }}
      >
        <Typography variant={"subtitle1"} sx={{ fontWeight: 600 }}>
          {"Classifications"}
        </Typography>
        <Disabled ifReadOnly>
          <Button
            size={"small"}
            startIcon={<AddIcon />}
            onClick={handleAdd}
            variant={"outlined"}
          >
            {"Add row"}
          </Button>
        </Disabled>
      </Stack>
      <TableContainer>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>{"Classification"}</TableCell>
              <TableCell>{"Premium Basis"}</TableCell>
              <TableCell align={"right"}>{"Exposure Amount"}</TableCell>
              <TableCell align={"right"}>{"Advance Premium"}</TableCell>
              <TableCell align={"right"}>{"Rate"}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: "text.secondary" }}>
                  {'No classifications yet. Click "Add row" to start.'}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => {
              const rate = glClassifications.computeRate({
                advancePremium: row.advancePremium,
                exposureAmount: row.exposureAmount,
                premiumBasis: row.premiumBasis,
              });
              return (
                <TableRow key={row.id}>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Disabled ifReadOnly>
                      <TextField
                        variant={"standard"}
                        fullWidth
                        value={row.classification ?? ""}
                        onChange={(e) =>
                          handleUpdate(row.id, {
                            classification: e.target.value,
                          })
                        }
                        placeholder={"e.g. Concrete Contractor"}
                      />
                    </Disabled>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Disabled ifReadOnly>
                      <TextField
                        select
                        variant={"standard"}
                        fullWidth
                        value={row.premiumBasis ?? "s"}
                        onChange={(e) =>
                          handleUpdate(row.id, {
                            premiumBasis: e.target.value,
                          })
                        }
                        SelectProps={{
                          MenuProps: {
                            disableScrollLock: true,
                            disableAutoFocusItem: true,
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            transformOrigin: {
                              vertical: "top",
                              horizontal: "left",
                            },
                          },
                        }}
                      >
                        {glClassifications.PREMIUM_BASIS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {glClassifications.formatPremiumBasis(opt.value)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Disabled>
                  </TableCell>
                  <TableCell align={"right"}>
                    <Disabled ifReadOnly>
                      <PrettyNumberTextField
                        variant={"standard"}
                        fullWidth
                        value={row.exposureAmount}
                        onChangeNumber={(exposureAmount) =>
                          handleUpdate(row.id, { exposureAmount })
                        }
                      />
                    </Disabled>
                  </TableCell>
                  <TableCell align={"right"}>
                    <Disabled ifReadOnly>
                      <PrettyNumberTextField
                        variant={"standard"}
                        fullWidth
                        value={row.advancePremium}
                        onChangeNumber={(advancePremium) =>
                          handleUpdate(row.id, { advancePremium })
                        }
                      />
                    </Disabled>
                  </TableCell>
                  <TableCell align={"right"}>
                    <Box>
                      <Typography variant={"body2"}>
                        {fmtRate(rate)}
                        {rate != null ? ` (${row.premiumBasis})` : ""}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align={"right"} sx={{ width: 40 }}>
                    <Disabled ifReadOnly>
                      <Tooltip title={"Remove row"}>
                        <IconButton
                          size={"small"}
                          onClick={() => handleRemove(row.id)}
                        >
                          <DeleteIcon fontSize={"small"} />
                        </IconButton>
                      </Tooltip>
                    </Disabled>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default GLClassificationsTable;
